import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Row, Col, Form } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { toast } from 'react-toastify';
import axios from 'axios';
import moment from 'moment';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../config/firebase';
import IconButton from 'components/common/IconButton';
import PageHeader from 'components/common/PageHeader';
import FalconComponentCard from 'components/common/FalconComponentCard';
import audiences from '../../config/audiences';
import FileUpload from 'components/doc-components/FileUpload';

const EditHomeBanner = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: '',
    image_file: null,
    image_filename: null,
    date_updated: null,
    date_added: null,
    date_expiry: null,
    post_date: null,
    audience: 'public',
    enabled: true,
    user_id: '',
  });
  const [fileUrl, setFileUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        const uid = user.uid;
        setFormData({
          ...formData,
          user_id: uid,
        });
      } else {
        navigate('/login');
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const eventId = window.location.pathname.split('/').pop();
      if (eventId) {
        try {
          const apiUrl = `${process.env.REACT_APP_API_URL}`;

          const response = await axios.get(`${apiUrl}/banners/id/${eventId}`);

          if (response.data) {
            const eventData = response.data.data[0];
            console.log('eventData', eventData);

            setFormData({
              title: eventData.title,
              enabled: eventData.enabled,
              audience: eventData.audience,
              user_id: eventData.user_id,
              image_file: null,
              doc_url: eventData.doc_url,
              image_filename: eventData.filename,
              file_type: eventData.file_type,
              id: eventData.id,
              year_added: eventData.year_added,
              date_added: moment
                .utc(eventData.date_added, 'YYYY-MM-DD HH:mm')
                .toDate(),
              post_date: moment
                .utc(eventData.post_date, 'YYYY-MM-DD HH:mm')
                .toDate(),
              date_expiry: moment
                .utc(eventData.date_expiry, 'YYYY-MM-DD HH:mm')
                .toDate(),
            });
            setFileUrl(eventData.doc_url);
            setLoading(false);
          }
        } catch (err) {
          console.log('Error', err);
          setLoading(false);
        }
      } else {
        navigate('/banners/list');
      }
    };

    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form data', formData);

    if (moment(formData.date_expiry).isBefore(formData.post_date)) {
      toast.error(`Expiry date must be after post date.`, {
        theme: 'colored',
      });
      return;
    }

    let formData2 = new FormData();
    formData2.append('title', formData.title);
    formData2.append('date_updated', moment.utc().format('YYYY-MM-DD HH:mm'));
    formData2.append(
      'post_date',
      moment.utc(formData.post_date).format('YYYY-MM-DD HH:mm')
    );
    formData2.append(
      'date_added',
      moment.utc(formData.date_added).format('YYYY-MM-DD HH:mm')
    );
    formData2.append('year_added', new Date(formData.date_added).getFullYear());
    formData2.append(
      'date_expiry',
      moment.utc(formData.date_expiry).format('YYYY-MM-DD HH:mm')
    );
    formData2.append('user_id', formData.user_id);
    formData2.append('audience', formData.audience);
    formData2.append('enabled', formData.enabled);

    formData2.append('image_filename', fileUrl);
    formData2.append('doc_url', fileUrl);
    formData2.append('fileUrl', fileUrl);

    try {
      const apiUrl = `${process.env.REACT_APP_API_URL}`;
      const id = window.location.pathname.split('/').pop();

      const response = await axios.put(`${apiUrl}/banners/id/${id}`, formData2);

      if (response) {
        toast.success(`Banner uploaded successfully`, {
          theme: 'colored',
        });
        navigate('/banners/list');
      }
    } catch (err) {
      toast.error(err.response?.data?.message ?? 'Error saving banner', {
        theme: 'colored',
        autoClose: false,
        closeButton: true,
        hideProgressBar: true,
      });
    }
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    if (name === 'post_date' || name === 'date_expiry') {
      const dateValue = moment.utc(value).format('YYYY-MM-DD HH:mm');
      setFormData({
        ...formData,
        [name]: dateValue,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  return (
    <>
      <PageHeader
        title='Edit sliding banner'
        description='This updates a banner image on the home page.'
        className='mb-3'
      ></PageHeader>

      <FalconComponentCard>
        <FalconComponentCard.Header
          title='Details'
          light={false}
          noPreview={true}
        ></FalconComponentCard.Header>
        {!loading && (
          <Form onSubmit={handleSubmit}>
            <FalconComponentCard.Body>
              <Form.Label>Title</Form.Label>
              <Form.Control
                size='lg'
                type='text'
                placeholder=''
                name='title'
                onChange={handleFieldChange}
                required
                autoComplete='off'
                value={formData.title}
              />
              <br />

              <Row>
                <Col lg='12'>
                  <Link to={formData.doc_url} target='_blank'>
                    Current PDF: {formData.image_filename}
                  </Link>
                </Col>
              </Row>

              <Row>
                <Col lg='6'>
                  <FileUpload category={'banners'} setFileUrl={setFileUrl} />
                </Col>

                <Col lg='6'>
                  <Form.Group>
                    <Form.Label>Audience</Form.Label>
                    <Form.Select
                      size='md'
                      className='mb-3'
                      name='audience'
                      onChange={handleFieldChange}
                      value={formData.audience}
                    >
                      {audiences.map((audience) => (
                        <option key={audience.value} value={audience.value}>
                          {audience.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group
                    controlId='formFileLg'
                    className='mb-3'
                    width='100%'
                  >
                    <Form.Label>Post date</Form.Label>
                    <br />
                    <DatePicker
                      selected={formData.post_date}
                      onChange={(value) =>
                        setFormData({
                          ...formData,
                          post_date: value,
                        })
                      }
                      className='form-control'
                      placeholderText='DD-MM-YYYY H:mm'
                      timeIntervals={60}
                      dateFormat='dd-MM-yyyy H:mm'
                      showTimeSelect
                      fixedHeight
                      width='100%'
                      size='lg'
                      name='post_date'
                      autoComplete='off'
                      required
                      value={formData.post_date}
                    />
                  </Form.Group>

                  <Form.Group
                    controlId='formFileLg'
                    className='mb-3'
                    width='100%'
                  >
                    <Form.Label>Expiry date</Form.Label>
                    <br />
                    <DatePicker
                      selected={formData.date_expiry}
                      onChange={(value) =>
                        setFormData({
                          ...formData,
                          date_expiry: value,
                        })
                      }
                      className='form-control'
                      placeholderText='DD-MM-YYYY H:mm'
                      timeIntervals={60}
                      dateFormat='dd-MM-yyyy H:mm'
                      showTimeSelect
                      fixedHeight
                      width='100%'
                      size='lg'
                      name='date_expiry'
                      autoComplete='off'
                      value={formData.date_expiry}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col lg='6' className='mt-4 mb-3 ml-3'>
                  <Form.Group>
                    <IconButton
                      className='mb-1'
                      variant='falcon-default'
                      size='lg'
                      icon='plus'
                      transform='shrink-3'
                      type='submit'
                    >
                      Submit
                    </IconButton>
                  </Form.Group>
                </Col>
              </Row>
            </FalconComponentCard.Body>
          </Form>
        )}
      </FalconComponentCard>
    </>
  );
};

export default EditHomeBanner;
